// Deep imports, not the core barrel: many tests mock '@smartmixin/core'
// wholesale, which would leave these allowlists undefined.
import {
  FAVORITE_KINDS,
  OTHER,
  SEARCH_SOURCES,
  connectParams,
  searchCountParams,
} from '@smartmixin/core/src/utils/Analytics/schema';
import type {
  ConnectSource,
  SearchCounts,
  SearchSource,
} from '@smartmixin/core/src/utils/Analytics/schema';
import type {ProviderId} from '@smartmixin/core';
import type {CoreAnalyticsEvent} from '@smartmixin/core/src/utils/Analytics/sink';
import ReactGA from 'react-ga4';

const allowlisted = (values: readonly string[], value: string) =>
  values.includes(value) ? value : OTHER;

const send = (name: string, params: Record<string, string>) => {
  try {
    ReactGA.gtag('event', name, params);
  } catch {
    // Analytics must never break the user's action.
  }
};

/**
 * An event core's shared hooks report (#379): `favorite_add` /
 * `favorite_remove` with `kind`, once the favourite has changed. Installed
 * as core's analytics sink in App.tsx.
 */
export const sendCoreAnalyticsEvent = (event: CoreAnalyticsEvent) => {
  send(event.name, {kind: allowlisted(FAVORITE_KINDS, event.params.kind)});
};

/**
 * A search (#379): GA `search_results` (not GA's recommended `search`, whose
 * `search_term` would carry the query) with `source` (places, stations, map) and a
 * bucketed count: `place_results` when a place is picked or none matched,
 * `station_results` when the stations near a picked place are listed. Never
 * the query, the place or a raw count.
 */
export const trackSearch = (source: SearchSource, counts: SearchCounts) => {
  const params = {
    source: allowlisted(SEARCH_SOURCES, source),
    ...searchCountParams(counts),
  };
  send('search_results', params);
};

/**
 * The user set off to connect a weather service (#379, 4c): GA
 * `connect_start` with `provider` and `source`, built by core's
 * `connectParams` as on mobile (core's schema test holds every provider to a
 * value).
 * Once per attempt, a first connect only (never a reconnect): the submit
 * that sends credentials which passed the form's checks, Netatmo's sign-in
 * click, the Weather Underground wizard's first valid submit.
 */
export const trackConnectStart = (
  provider: ProviderId,
  source?: ConnectSource,
) => {
  send('connect_start', connectParams(provider, source));
};

/**
 * The service is connected with a station to show: GA `connect_success`,
 * same parameters. An account linked with no station yet sends nothing.
 * Netatmo counts once the link is stored, its stations syncing on the
 * backend afterwards.
 */
export const trackConnectSuccess = (
  provider: ProviderId,
  source?: ConnectSource,
) => {
  send('connect_success', connectParams(provider, source));
};
